/**
 * Form Entity Schemas
 *
 * Schema-driven (Puck-authored) forms with public-link distribution and
 * optional anonymous submissions. The actual block schema lives in
 * `./form-schema.ts`; this file describes API surfaces around it.
 */

import { z } from 'zod'

// ============================================
// Enums
// ============================================

export const FormStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'CLOSED', 'ARCHIVED'])
export type FormStatus = z.infer<typeof FormStatusSchema>

export const SignatureTypeSchema = z.enum(['DRAWN', 'TYPED'])
export type SignatureType = z.infer<typeof SignatureTypeSchema>

export const FormDistributionModeSchema = z.enum(['RECIPIENTS', 'PUBLIC_LINK', 'BOTH'])
export type FormDistributionMode = z.infer<typeof FormDistributionModeSchema>

export const FormSubmitterTypeSchema = z.enum(['AUTHENTICATED_USER', 'ANONYMOUS'])
export type FormSubmitterType = z.infer<typeof FormSubmitterTypeSchema>

// ============================================
// Target Group
// ============================================

export const FormTargetGroupSchema = z.object({
  type: z.literal('group'),
  id: z.string(),
  name: z.string()
})
export type FormTargetGroup = z.infer<typeof FormTargetGroupSchema>

// ============================================
// Form Template (API response)
// ============================================

export const FormTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  schema: z.unknown(),
  createdAt: z.string()
})
export type FormTemplate = z.infer<typeof FormTemplateSchema>

// ============================================
// Form (API response - list item)
// ============================================

export const FormListItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: FormStatusSchema,
  distributionMode: FormDistributionModeSchema,
  publicSlug: z.string().nullable(),
  dueDate: z.string().nullable(),
  sentAt: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.object({
    name: z.string(),
    email: z.string().nullable()
  })
})
export type FormListItem = z.infer<typeof FormListItemSchema>

// ============================================
// Form (API response - detail)
// ============================================

export const FormDetailSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  status: FormStatusSchema,
  schema: z.unknown(),
  distributionMode: FormDistributionModeSchema,
  publicSlug: z.string().nullable(),
  allowAnonymous: z.boolean(),
  targetGroups: z.array(FormTargetGroupSchema),
  dueDate: z.string().nullable(),
  sentAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  createdBy: z.object({
    name: z.string(),
    email: z.string().nullable()
  }),
  createdAt: z.string()
})
export type FormDetail = z.infer<typeof FormDetailSchema>

// ============================================
// API Request Schemas
// ============================================

export const CreateFormRequestSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  templateId: z.string().optional(),
  targetGroups: z.array(FormTargetGroupSchema).default([]),
  dueDate: z.string().datetime().optional(),
  schema: z.unknown(),
  distributionMode: FormDistributionModeSchema.optional(),
  allowAnonymous: z.boolean().optional()
})
export type CreateFormRequest = z.infer<typeof CreateFormRequestSchema>

export const UpdateFormRequestSchema = z.object({
  title: z.string().min(1).optional(),
  description: z.string().optional(),
  targetGroups: z.array(FormTargetGroupSchema).optional(),
  dueDate: z.string().datetime().nullable().optional(),
  schema: z.unknown().optional(),
  distributionMode: FormDistributionModeSchema.optional(),
  allowAnonymous: z.boolean().optional()
})
export type UpdateFormRequest = z.infer<typeof UpdateFormRequestSchema>

export const CreateFormTemplateRequestSchema = z.object({
  name: z.string().min(1, 'Template name is required'),
  description: z.string().optional(),
  schema: z.unknown()
})
export type CreateFormTemplateRequest = z.infer<typeof CreateFormTemplateRequestSchema>
